import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { Redirect } from "react-router-dom";
import {
  FaCheck,
  FaCheckCircle,
  FaLock,
  FaSortAlphaDown,
  FaSortNumericDown,
  FaTimes,
  FaUnlock,
  FaUserGraduate,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import SelectClass from "../ChildComponent/SelectClass";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/modal.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

function isStudentLocked(student) {
  return (
    student?.fee_collected === true ||
    student?.fee_collected === 1 ||
    student?.admission_type_locked === true ||
    student?.admission_type_locked === 1
  );
}

function studentInitials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

/**
 * Multi-student picker that locks students when fee is collected or settled
 * (same batch rule as ManageAdmissionType / getCurrentStudentIfFeeCollected).
 * Created By Cursor
 */
const MdlSelectMultipleStudentFeeLocked = ({
  actionClose,
  actionHandleSelected,
  title = "Select Students",
}) => {
  const [loggedIn] = useState(localStorage.getItem("token") !== null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [show] = useState(true);
  const [selectedClassPosition, setSelectedClassPosition] = useState(0);
  const [studentList, setStudentList] = useState([]);
  const [mSelectedStudent, setMSelectedStudent] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [classMeta, setClassMeta] = useState({
    classId: "",
    sectionId: "",
    sessionId: "",
  });

  useEffect(() => {
    localStorage.setItem("SelectedMultipleStudent", JSON.stringify([]));
  }, []);

  const selectableStudents = useMemo(
    () => studentList.filter((s) => !isStudentLocked(s)),
    [studentList]
  );

  const lockedCount = useMemo(
    () => studentList.filter((s) => isStudentLocked(s)).length,
    [studentList]
  );

  const getStudentList = useCallback((class_id, section_id, session_id) => {
    setLoadingStudent(true);
    setStudentList([]);

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const params = {
      school_code: SCHOOL_CODE,
      class_id,
      section_id,
      session_id,
    };

    axios
      .get(`${API_BASE()}getCurrentStudentIfFeeCollected.php`, { params })
      .then((res) => res.data)
      .then(async (response) => {
        if (response.api_status === "OK" && Array.isArray(response.data)) {
          setStudentList(response.data);
          setLoadingStudent(false);
          return;
        }

        // Fallback: plain list + batch fee/settlement check
        const fallback = await axios.get(`${API_BASE()}getCurrentStudent.php`, {
          params,
        });
        const payload = fallback?.data;
        if (payload?.api_status !== "OK" || !Array.isArray(payload.data)) {
          setLoadingStudent(false);
          myToast.notify_warning(
            response.api_remark || payload?.api_remark || "No students found"
          );
          setStudentList([]);
          return;
        }

        let rows = payload.data;
        try {
          const ids = rows
            .map((r) => r.student_enrollment_no)
            .filter(Boolean)
            .join(",");
          if (ids) {
            const feeRes = await axios.get(
              `${API_BASE()}checkIfStudentFeeCollectedBatch.php`,
              {
                params: {
                  student_ids: ids,
                  class_id,
                  session_id,
                  school_code: SCHOOL_CODE,
                },
              }
            );
            const feeData = feeRes?.data?.data;
            const feeMap = {};
            if (Array.isArray(feeData)) {
              feeData.forEach((item) => {
                const id = item.student_id || item.student_enrollment_no || "";
                if (id) {
                  feeMap[id] =
                    item.fee_collected === true || item.fee_collected === 1;
                }
              });
            } else if (feeData && typeof feeData === "object") {
              Object.keys(feeData).forEach((id) => {
                feeMap[id] = feeData[id] === true || feeData[id] === 1;
              });
            }
            rows = rows.map((row) => {
              const collected = Boolean(feeMap[row.student_enrollment_no]);
              return {
                ...row,
                fee_collected: collected,
                admission_type_locked: collected,
              };
            });
          }
        } catch (feeError) {
          console.warn(
            "[MdlSelectMultipleStudentFeeLocked] fee batch check failed",
            feeError
          );
        }

        setStudentList(rows);
        setLoadingStudent(false);
      })
      .catch((error) => {
        setLoadingStudent(false);
        console.error(
          "[MdlSelectMultipleStudentFeeLocked] fetch error:",
          error
        );
        myToast.notify_warning("Failed to load students.");
      });
  }, []);

  const callBackClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position
    ) => {
      setMSelectedStudent([]);
      localStorage.setItem("SelectedMultipleStudent", JSON.stringify([]));
      setSelectAll(false);
      setSortBy(null);
      setSelectedClassPosition(selected_position);
      setClassMeta({
        classId: class_id,
        sectionId: section_id,
        sessionId: session_id,
      });
      getStudentList(class_id, section_id, session_id);
    },
    [getStudentList]
  );

  const syncSelectAll = useCallback((selected, selectable) => {
    setSelectAll(
      selectable.length > 0 && selected.length === selectable.length
    );
  }, []);

  const handleStudentSelection = useCallback(
    (student, forceChecked) => {
      if (isStudentLocked(student)) {
        myToast.notify_warning(
          "Cannot select: fee collection or settlement is already done for this student."
        );
        return;
      }

      const isSelected = mSelectedStudent.some(
        (s) => s.student_enrollment_no === student.student_enrollment_no
      );
      const nextChecked =
        typeof forceChecked === "boolean" ? forceChecked : !isSelected;

      let updated = [...mSelectedStudent];
      if (nextChecked) {
        if (!isSelected) updated.push(student);
      } else {
        updated = updated.filter(
          (s) => s.student_enrollment_no !== student.student_enrollment_no
        );
      }

      setMSelectedStudent(updated);
      localStorage.setItem(
        "SelectedMultipleStudent",
        JSON.stringify(updated)
      );
      syncSelectAll(updated, selectableStudents);
    },
    [mSelectedStudent, selectableStudents, syncSelectAll]
  );

  const handleSelectAll = useCallback(() => {
    if (!selectAll) {
      setMSelectedStudent(selectableStudents);
      localStorage.setItem(
        "SelectedMultipleStudent",
        JSON.stringify(selectableStudents)
      );
      setSelectAll(true);
    } else {
      setMSelectedStudent([]);
      localStorage.setItem("SelectedMultipleStudent", JSON.stringify([]));
      setSelectAll(false);
    }
  }, [selectAll, selectableStudents]);

  const handleSort = useCallback(
    (sortType) => {
      if (sortBy === sortType) {
        setStudentList((prev) => [...prev].reverse());
        return;
      }
      const sorted = [...studentList].sort((a, b) => {
        if (sortType === "name") {
          return (a.stu_name || "").localeCompare(b.stu_name || "");
        }
        if (sortType === "rollNo") {
          return (
            (parseInt(a.student_roll_no, 10) || 0) -
            (parseInt(b.student_roll_no, 10) || 0)
          );
        }
        return 0;
      });
      setStudentList(sorted);
      setSortBy(sortType);
    },
    [sortBy, studentList]
  );

  const handleDone = useCallback(() => {
    const unlocked = mSelectedStudent.filter((s) => !isStudentLocked(s));
    if (unlocked.length === 0) {
      myToast.notify_warning("Please select at least one unlocked student");
      return;
    }
    actionHandleSelected(unlocked);
  }, [mSelectedStudent, actionHandleSelected]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div>
      <style>
        {`
          .mdl-fee-locked-dialog {
            max-width: 1200px !important;
            width: 95% !important;
            max-height: 90vh !important;
          }
          .mdl-fee-locked-dialog .modal-content {
            border-radius: 16px;
            overflow: hidden;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
          }
          .mdl-fee-locked-dialog .modal-body {
            flex: 1 1 auto;
            overflow-y: auto;
          }
        `}
      </style>
      <Modal
        show={show}
        onHide={actionClose}
        animation={false}
        className="hide-scrollbar"
        dialogClassName="modal-dialog-centered mdl-fee-locked-dialog"
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header
          className="mdl-fee-locked__header"
          style={{
            background: COLORS.primary_color_shade5,
            color: COLORS.white,
          }}
        >
          <Modal.Title>{title}</Modal.Title>
          <div className="mdl-fee-locked__tools">
            <span className="mdl-fee-locked__count">
              Selected: {mSelectedStudent.length}
              {lockedCount > 0 ? ` · Locked: ${lockedCount}` : ""}
            </span>

            <div className="mdl-fee-locked__sort">
              <span>Sort:</span>
              <button
                type="button"
                className={`mdl-fee-locked__chip${
                  sortBy === "name" ? " is-active" : ""
                }`}
                onClick={() => handleSort("name")}
              >
                <FaSortAlphaDown aria-hidden /> Name
              </button>
              <button
                type="button"
                className={`mdl-fee-locked__chip${
                  sortBy === "rollNo" ? " is-active" : ""
                }`}
                onClick={() => handleSort("rollNo")}
              >
                <FaSortNumericDown aria-hidden /> Roll
              </button>
            </div>

            <button
              type="button"
              className="mdl-fee-locked__chip is-active"
              onClick={handleSelectAll}
              disabled={selectableStudents.length === 0}
              title={
                lockedCount > 0
                  ? "Selects only unlocked students"
                  : undefined
              }
            >
              {selectAll ? "Deselect All" : "Select All"}
              {lockedCount > 0 && !selectAll
                ? ` (${selectableStudents.length})`
                : ""}
            </button>

            <button
              type="button"
              className="mdl-fee-locked__done"
              onClick={handleDone}
              disabled={mSelectedStudent.length === 0}
            >
              <FaCheckCircle aria-hidden /> Done
            </button>

            <button
              type="button"
              className="mdl-fee-locked__close"
              onClick={actionClose}
              aria-label="Close"
              title="Close"
            >
              <FaTimes aria-hidden />
            </button>
          </div>
        </Modal.Header>

        <Modal.Body className="hide-scrollbar mdl-fee-locked__body">
          <SelectClass
            action={callBackClassSelect}
            selected_position={selectedClassPosition}
          />

          {!classMeta.classId ? (
            <div className="mdl-fee-locked__empty">
              <FaUserGraduate aria-hidden />
              <p>Select a class to load students</p>
            </div>
          ) : loadingStudent ? (
            <div className="mdl-fee-locked__loading">
              <MyLoader />
            </div>
          ) : studentList.length === 0 ? (
            <div className="mdl-fee-locked__empty">
              <FaUserGraduate aria-hidden />
              <p>No students found in this class</p>
            </div>
          ) : (
            <div className="mdl-fee-locked__grid">
              {studentList.map((item) => {
                const locked = isStudentLocked(item);
                const isSelected = mSelectedStudent.some(
                  (s) =>
                    s.student_enrollment_no === item.student_enrollment_no
                );
                const classLabel =
                  item.section_id === "No"
                    ? item.class_name
                    : `${item.class_name || ""}:${item.section_name || ""}`;

                return (
                  <button
                    type="button"
                    key={item.student_enrollment_no || item.id}
                    className={`mdl-fee-locked__card${
                      isSelected ? " is-selected" : ""
                    }${locked ? " is-locked" : ""}`}
                    onClick={() => handleStudentSelection(item)}
                    disabled={locked}
                    title={
                      locked
                        ? "Fee collected or settled — selection locked"
                        : undefined
                    }
                  >
                    {isSelected ? (
                      <span className="mdl-fee-locked__check" aria-hidden>
                        <FaCheck />
                      </span>
                    ) : null}

                    <div className="mdl-fee-locked__avatar" aria-hidden>
                      {item.student_img_path ? (
                        <img
                          src={item.student_img_path}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : null}
                      <span>{studentInitials(item.stu_name)}</span>
                    </div>

                    <div className="mdl-fee-locked__info">
                      <div className="mdl-fee-locked__name">
                        {item.stu_name || "—"}
                      </div>
                      {item.father_name ? (
                        <div className="mdl-fee-locked__father">
                          S/O {item.father_name}
                        </div>
                      ) : null}
                      <div className="mdl-fee-locked__chips">
                        <span>
                          Roll: {item.student_roll_no || "—"}
                        </span>
                        <span>{classLabel || "—"}</span>
                        {item.sr_admission_no ? (
                          <span>SRN: {item.sr_admission_no}</span>
                        ) : null}
                        {locked ? (
                          <span className="mdl-fee-locked__lock-chip">
                            <FaLock aria-hidden /> Locked
                          </span>
                        ) : (
                          <span className="mdl-fee-locked__unlock-chip">
                            <FaUnlock aria-hidden /> Unlocked
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MdlSelectMultipleStudentFeeLocked;
